/* eslint-disable @typescript-eslint/no-unused-vars */
import { TEXT_LINK_FOCUS_RING } from '../lib/a11yFocus'
import { currencyLabel } from '../lib/currency'
import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { Button, InlineAlert, SearchableSelect, SearchInput } from '../components/BaseComponents'
import routes from '../components/Router/routes'
import PageHeadings from '../components/BaseComponents/PageHeadings/PageHeadings'
import PageTemplate from '../components/BaseComponents/PageTemplate/PageTemplate'
import { countries } from '../assets/countryList'
import Pagination from '../components/BaseComponents/Pagination/Pagination'
import useApi from '../hooks/useApi'
import useCleanup from '../hooks/useCleanup'
import SimpleSortTable, { TableColumn } from '../components/BaseComponents/SimpleSortTable/SimpleSortTable'
import AdminCreateCompany from '../components/Admin/CreateCompany/CreateCompany'
import { generatePath, Link } from 'react-router-dom'
import Label from '../components/BaseComponents/Label'
import { useDebouncedFilters } from '../hooks/useDebouncedFilters'
import usePagination from '../hooks/usePagination'
import useDataSort from '../hooks/useDataSort'
import NumericText from '../components/BaseComponents/NumericText/NumericText'
import { getApiErrorMessage } from '../lib/utils'

const columns: TableColumn[] = [
	{
		id: 'settings',
		label: 'Settings',
		renderer: (key, row) => (
			<Button
				type='Primary'
				isLink
				hasLeadingIcon
				size='sm'
				icon="fa-solid fa-sliders"
				url={`/admin/companies/${row.id}`}
			/>
		)
	},
	{
		id: 'name',
		label: 'Name',
		sortable: true,
		renderer: (key, row) => {
			const controlPath = generatePath(
				routes.assumeOrganisation.path,
				{assumptionType: 'admin', companyId: row.id}
			)

			const hasEntryPermission = row['admin_entry_permission']
			return hasEntryPermission ? (
				<Link
					className={`text-link ${TEXT_LINK_FOCUS_RING}`}
					to={controlPath}
				>
					{ row[key]}
				</Link>
			) : (
				<span>{row[key]}</span>
			)
		}
	},
	{
		id: 'number',
		label: 'Number',
		sortable: true,
	},
	{
		id: 'address',
		label: 'Address',
		renderer: (key, data) => <p title={data[key]} className="truncate w-32">{ data[key] }</p>
	},
	{
		id: 'jurisdiction_code',
		label: 'Jurisdiction',
		sortable: true,
	},
	{
		id: 'is_public_body',
		label: 'Public Body',
		sortable: true,
		renderer: (key, data) => <span>{ data[key] ? 'Yes' : 'No' } </span>
	},
	{
		id: 'is_registered',
		label: 'Registered',
		sortable: true,
		renderer: (key, data) => <span>{ data[key] ? 'Yes' : 'No' }</span>
	},
	{
		id: 'is_financial_institution',
		label: 'Financial Institution',
		sortable: true,
		renderer: (key, data) => <span>{ data[key] ? 'Yes' : 'No' }</span>
	},
	{
		id: 'currency_code',
		label: 'Currency',
		sortable: true,
		renderer: (key, data) => <span>{ currencyLabel(data[key]) }</span>
	},
	{
		id: 'tier',
		label: 'Tier',
		sortable: true,
	},
	{
		id: 'status',
		label: 'Status',
		renderer: (key, data) =>
			<span>{ data['category'] === 'fictitious' ? `Fictitious (${data['fictitious_kind']})` : 'Real' }</span>
	},
	{
		id: 'users_count',
		label: 'Users',
		sortable: true,
		renderer: (key, data) => <NumericText value={data[key]} />,
	},
	{
		id: 'admin_locked',
		label: 'Locked',
		sortable: true,
		renderer: (key, data) => <span>{ data[key] ? 'Yes' : 'No' }</span>
	}
];

const tierOptions = [
	{ value: 'any', label: 'Any' },
	{ value: 'micro', label: 'Micro' },
	{ value: 'standard', label: 'Standard' },
	{ value: 'premium', label: 'Premium' },
]

const publicBodyOptions = [
	{ value: 'any', label: 'Any' },
	{ value: 'yes', label: 'Yes' },
	{ value: 'no', label: 'No' },
]

const setupStatusOptions = [
	{ value: 'any', label: 'Any' },
	{ value: 'no', label: 'Not set up' },
	{ value: 'yes', label: 'Set up' },
]

const invitationStatusOptions = [
	{ value: 'any', label: 'Any' },
	{ value: 'None', label: 'None' },
	{ value: 'Sent', label: 'Sent' },
	{ value: 'Accepted', label: 'Accepted' },
]

const statusOptions = [
	{ value: 'any', label: 'Any' },
	{ value: 'real', label: 'Real' },
	{ value: 'fictitious', label: 'Fictitious' },
]

function AdminCompanies() {
	const countryList = countries

	const [loading, setLoading] = useState(true);

	const pagination = usePagination(1, 20);
	const api = useApi();

	const [filters, setFilters] = useState({
		name: '',
		number: '',
		jurisdiction: 'any',
		publicBody: 'any',
		registered: 'any',
		financialInstitution: 'any',
		setupStatus: 'any',
		invitationStatus: 'any',
		tier: 'any',
		status: 'any',
		currencyCode: 'any',
	})

	const [data, setData] = useState<any[]>([]);

	const [dynamicFacets, setDynamicFacets] = useState({
		currencyCodes: [] as string[],
		jurisdictions: [] as string[],
	});
	const [facetsError, setFacetsError] = useState<any>();
	const [loadError, setLoadError] = useState<any>();

	const jurisdictionOptions = [
		{ value: 'any', label: 'Any' },
		...countryList
			.filter((country) => dynamicFacets.jurisdictions.includes(country['value']))
			.map((country) => ({ value: country.value, label: country.label })),
	]

	const currencyOptions = [
		{ value: 'any', label: 'Any' },
		...dynamicFacets.currencyCodes.map((currencyCode) => ({
			value: currencyCode,
			label: currencyLabel(currencyCode),
		})),
	]

	// Issue #884: this GET sits behind Auth/RequireWriteAccess and can receive the now-rejecting
	// ACCESS_AUDIT_UNAVAILABLE (#857 item 6), or any other failure, instead of hanging forever (and
	// instead of the unhandled rejection the un-caught `run()` call used to leave behind). It's
	// independent of the main companies list load below, so its own failure - and its own retry -
	// must not affect the main list's data/loading/error state at all. On failure, the dropdown
	// options already on screen (if any) are left exactly as they were rather than being reset to
	// an empty/successful-looking state.
	const loadFacets = useCleanup(() => {
		let cancelled = false;
		const run = async () => {
			const apiResult = await api.get('admin/companiesSearchFacets');
			if(cancelled) {
				return;
			}
			setDynamicFacets(apiResult.data);
			setFacetsError(undefined);
		}
		run().catch((err) => {
			if(cancelled) {
				return;
			}
			setFacetsError(err);
		})

		return () => { cancelled = true }
	})

	useEffect(() => {
		loadFacets()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const dataSort = useDataSort('name', 'asc');

	// Issue #884: onSearch previously had no catch at all, so a rejecting GET left `loading` stuck
	// true forever, left the table showing whatever (possibly empty/stale) data it already had as
	// if that were the real result, and produced an unhandled rejection. useCleanup also makes the
	// cancellation flag apply on unmount automatically, since useDebouncedFilters' debounce timer
	// and flush() both discard the cleanup function this used to return.
	const onSearch = useCleanup(() => {
		let cancelled = false;
		setLoading(true);
		const run = async () => {
			const qs = new URLSearchParams({
				...filters,
				...pagination.params,
				...dataSort.params,
			});
			const results = await api.get('admin/companies?' + qs.toString());
			if(cancelled) {
				return
			}
			setData(results.data.companies);
			pagination.readMeta(results.data.meta)
			setLoadError(undefined);
			setLoading(false);
		}

		run().catch((err) => {
			if(cancelled) {
				return;
			}
			setLoading(false);
			setLoadError(err);
		})

		return () => {
			cancelled = true;
		}
	})

	const { searchInProgress, flush: flushSearch } = useDebouncedFilters(filters, onSearch)

	useEffect(() => {
		onSearch();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pagination.params, dataSort.params])

	const [addCompanyModalOpen, setAddCompanyModalOpen] = useState<boolean>(false)

	return (
		<PageTemplate testid='add-accounting-periods-main'>
			<Helmet>
				<title>{routes.adminCompanies.name}</title>
			</Helmet>
			<PageHeadings title='Create Organisation' />

			{facetsError && (
				<InlineAlert type='warning' className='mt-4' data-testid='admin-companies-facets-error'>
					{getApiErrorMessage(facetsError, 'Failed to load filter options. Please try again.')}
					{' '}
					<Button type='Secondary' size='sm' text='Retry facets' additionalClass='ml-2' onClick={() => loadFacets()} />
				</InlineAlert>
			)}

			<div className='block'>
				<Button
					type='Primary'
					text='Create a New Organisation'
					size='sm'
					hasTrailingIcon
					icon='fa-solid fa-plus'
					onClick={() => {
						setAddCompanyModalOpen(true)
					}}
					additionalClass='mt-4 mb-2'
				/>
				<AdminCreateCompany
					open={addCompanyModalOpen}
					onClose={() => setAddCompanyModalOpen(false)}
				/>
			</div>

			<div className="my-8">
				<div className="flex flex-wrap gap-3">
					<SearchInput
						name="name"
						label="Name"
						placeholder="iSumio..."
						value={filters.name}
						onChange={e => setFilters({...filters, name: e.target.value})}
						onSearch={flushSearch}
						onClear={() => setFilters({...filters, name: ''})}
						isLoading={searchInProgress}
					/>
					<SearchInput
						name="number"
						label="Organisation Number"
						placeholder="123..."
						value={filters.number}
						onChange={e => setFilters({...filters, number: e.target.value})}
						onSearch={flushSearch}
						onClear={() => setFilters({...filters, number: ''})}
						isLoading={searchInProgress}
					/>
					<label>
						<Label className="block">Tier</Label>
						<SearchableSelect
							isSearchable={false}
							options={tierOptions}
							value={tierOptions.find((option) => option.value === filters.tier)}
							onChange={(option) => setFilters({...filters, tier: option?.value ?? 'any'})}
						/>
					</label>
				</div>
				<div className="flex flex-wrap gap-3 my-3">
					<label>
						<Label className="block">Jurisdiction</Label>
						<SearchableSelect
							options={jurisdictionOptions}
							value={jurisdictionOptions.find((option) => option.value === filters.jurisdiction)}
							onChange={(option) => setFilters({...filters, jurisdiction: option?.value ?? 'any'})}
						/>
					</label>
					<label>
						<Label className="block">Public Body</Label>
						<SearchableSelect
							isSearchable={false}
							options={publicBodyOptions}
							value={publicBodyOptions.find((option) => option.value === filters.publicBody)}
							onChange={(option) => setFilters({...filters, publicBody: option?.value ?? 'any'})}
						/>
					</label>
					<label>
						<Label className="block">Registered</Label>
						<SearchableSelect
							isSearchable={false}
							options={publicBodyOptions}
							value={publicBodyOptions.find((option) => option.value === filters.registered)}
							onChange={(option) => setFilters({...filters, registered: option?.value ?? 'any'})}
						/>
					</label>
					<label>
						<Label className="block">Financial Institution</Label>
						<SearchableSelect
							isSearchable={false}
							options={publicBodyOptions}
							value={publicBodyOptions.find((option) => option.value === filters.financialInstitution)}
							onChange={(option) => setFilters({...filters, financialInstitution: option?.value ?? 'any'})}
						/>
					</label>
					<label>
						<Label className="block">Set Up</Label>
						<SearchableSelect
							isSearchable={false}
							options={setupStatusOptions}
							value={setupStatusOptions.find((option) => option.value === filters.setupStatus)}
							onChange={(option) => setFilters({...filters, setupStatus: option?.value ?? 'any'})}
						/>
					</label>

					<label>
						<Label className="block">Invitation Status</Label>
						<SearchableSelect
							isSearchable={false}
							isDisabled
							options={invitationStatusOptions}
							value={invitationStatusOptions.find((option) => option.value === filters.invitationStatus)}
							onChange={(option) => setFilters({...filters, invitationStatus: option?.value ?? 'any'})}
						/>
					</label>

					<label>
						<Label className="block">Currency</Label>
						<SearchableSelect
							options={currencyOptions}
							value={currencyOptions.find((option) => option.value === filters.currencyCode)}
							onChange={(option) => setFilters({...filters, currencyCode: option?.value ?? 'any'})}
						/>
					</label>

					<label>
						<Label className="block">Status</Label>
						<SearchableSelect
							isSearchable={false}
							options={statusOptions}
							value={statusOptions.find((option) => option.value === filters.status)}
							onChange={(option) => setFilters({...filters, status: option?.value ?? 'any'})}
						/>
					</label>

				</div>

				<Button
					type='Primary'
					text='Search'
					size='sm'
					hasTrailingIcon
					onClick={flushSearch}
					additionalClass='mt-4 mb-2'
				/>
			</div>
			{loadError ? (
				<>
					<InlineAlert type='error' className='mt-4' data-testid='admin-companies-load-error'>
						{getApiErrorMessage(loadError, 'Failed to load companies. Please try again.')}
					</InlineAlert>
					<Button type='Secondary' text='Retry' additionalClass='mt-4' onClick={() => onSearch()} />
				</>
			) : (
				<>
					<SimpleSortTable
						isLoading={loading || searchInProgress}
						columns={columns}
						data={data}
						defaultSorting={dataSort.defaultSorting}
						onSortPress={dataSort.onChange}
					/>
					<Pagination
						isLoading={loading}
						pagination={pagination.pagination}
						onPageChange={pagination.onPageChange}
					/>
				</>
			)}

		</PageTemplate>
	)
}
export default AdminCompanies
